<template>
    <div class="infinite-menu" :class="{ 'no-hover': !isStylePreview, 'inline-edit-mode': enableInlineEdit }" :style="densityVars">
        <div class="frame" :style="frameStyles">
            <div v-if="showBackButton" class="back-btn" :style="backBtnStyles">
                <div class="back-icon-wrapper" :style="backIconStyles">
                    <component :is="icons.back2" class="back-icon" :color="backIconColor" />
                </div>
            </div>

            <div
                class="title-bar"
                :style="titleBarStyles"
                :class="{ 'clickable-title': enableInlineEdit }"
                @click="enableInlineEdit ? focusTitleText($event) : null"
            >
                <div
                    ref="titleTextRef"
                    v-show="true"
                    class="title-text editable-text"
                    :class="{ 'is-editable': enableInlineEdit }"
                    :style="titleTextStyles"
                    :contenteditable="enableInlineEdit"
                    @blur="updateTitle"
                    @input="(e) => handleTextFieldInput('titleText', e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ displayTitleText }}</div>
            </div>

            <div class="grid" :style="gridStyles">
                <div
                    v-for="(opt, i) in visibleOptions"
                    :key="`${currentPage}-${i}`"
                    v-show="opt.enabled !== false"
                    class="cell editable-text"
                    :class="{
                        'is-editable': enableInlineEdit,
                        'cell-disabled': opt.enabled === false
                    }"
                    :style="getOptionButtonStyles(opt)"
                    :ref="`buttonRef${i}`"
                    :contenteditable="enableInlineEdit && opt.enabled !== false"
                    @blur="(e) => updateButton(i, e)"
                    @input="(e) => handleTextFieldInput(`buttonText${i}`, e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ getDisplayButtonText(i) }}
                </div>
            </div>

            <div class="pager" :style="pagerRowStyles">
                <div class="nav prev" :style="navBtnStyles" @click="goPrev" :aria-disabled="currentPage === 1" :class="{ 'disabled': currentPage === 1 }">
                    <div class="nav-icon-wrapper" :style="prevIconStyles">
                        <component :is="icons.back" class="nav-icon" />
                    </div>
                </div>
                <div class="page-numbers" :style="pageNumbersStyles">
                    <div
                        v-for="page in totalPages"
                        :key="page"
                        class="page-num"
                        :class="{ active: page === currentPage }"
                        :style="getPageNumStyle(page)"
                        @click="goToPage(page)"
                    >
                        {{ page }}
                    </div>
                </div>
                <div class="nav next" :style="navBtnStyles" @click="goNext" :aria-disabled="currentPage === totalPages" :class="{ 'disabled': currentPage === totalPages }">
                    <div class="nav-icon-wrapper" :style="nextIconStyles">
                        <component :is="icons.back" class="nav-icon" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import icons from './menu-icons';
import menuMixin from './menu-mixin';
import menuReactivityMixin from './menu-reactivity-mixin';
const Back2SVG = require('!raw-loader!../assets/icons/back2.svg');

const PAGE_NUMBERS_STYLES = {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    maxWidth: "300px"
};

export default {
    name: "MenuInfiniteOption",
    mixins: [menuMixin, menuReactivityMixin],
    props: {
        styleData: {type: Object, default: () => ({})},
        menuData: {type: Object, default: () => ({})},
        isStylePreview: {type: Boolean, default: false},
        isPreview: {type: Boolean, default: false},
        enableInlineEdit: {type: Boolean, default: false}
    },
    data() {
        return {
            icons: icons,
            currentPage: 1,
            pageSize: 12,
            pageNumbersStyles: PAGE_NUMBERS_STYLES
        };
    },
    computed: {
        titleText() {
            return this.cleanText(this.menuData.titleText);
        },
        displayTitleText() {
            const text = this.titleText;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "Menu Title Goes Here";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        allOptions() {
            let options = this.menuData.buttons || [];

            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit && options.length > 0) {
                return options.map((opt, index) => {
                    const trimmedText = (opt.text && opt.text.trim()) || "";
                    return {
                        ...opt,
                        text: trimmedText || (opt.enabled !== false ? `Button ${index + 1}` : "")
                    };
                });
            }

            return options;
        },
        showBackButton() {
            return this.menuData.backButton?.enabled !== false;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.allOptions.length / this.pageSize));
        },
        visibleOptions() {
            const start = (this.currentPage - 1) * this.pageSize;
            let options = this.allOptions.slice(start, start + this.pageSize);

            if (!this.enableInlineEdit && !this.isStylePreview) {
                options = options.filter(opt =>
                    opt.enabled !== false &&
                    opt.text &&
                    (typeof opt.text !== 'string' || opt.text.trim().length > 0)
                );
            }

            return options;
        },
        visibleButtonTexts() {
            if (!this.visibleOptions || !Array.isArray(this.visibleOptions)) {
                return [];
            }
            return this.visibleOptions.map(opt => {
                const text = opt?.text || '';
                const cleaned = text ? String(text).replace(/\u200B/g, '').trim() : '';
                return cleaned || (this.enableInlineEdit ? ' ' : '');
            });
        },

        densityVars() {
            if (!this.isStylePreview) return {};
            return {
                "--base-font": "0.9rem",
                "--title-scale": "1.6",
                "--btn-scale": "0.95",
            };
        },

        effective() {
            const d = {
                titleScreenBackgroundColor: {r: 44, g: 62, b: 80, a: 1},
                titleColor: {r: 255, g: 255, b: 255, a: 1},
                textColor: {r: 235, g: 244, b: 248, a: 0.95},
                buttonBackgroundColor: {r: 33, g: 122, b: 145, a: 0.95},
                buttonHoverColor: {r: 52, g: 192, b: 32, a: 1},
                buttonTextColor: {r: 255, g: 255, b: 255, a: 1},
                outlineColor: {r: 255, g: 255, b: 255, a: 0.9},
                opacity: 1.0
            };
            return {...d, ...this.styleData};
        },

        frameStyles() {
            const backW = this.isStylePreview ? "calc(66px * var(--element-scale, 1))" : "75px";
            const rowGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const columnGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const transform = this.isPreview ? "translateX(-3%)" : "none";
            return {
                display: "grid",
                gridTemplateColumns: `${backW} 1fr`,
                columnGap: columnGap,
                rowGap,
                width: "100%",
                maxWidth: "1040px",
                margin: this.isStylePreview ? "calc(16px * var(--gap-scale, 1)) auto" : "16px auto",
                transform: transform
            };
        },
        backBtnStyles() {
            const width = this.isStylePreview ? "calc(66px * var(--element-scale, 1))" : "75px";
            const height = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "56px";
            const fontSize = this.isStylePreview ? "calc(24px * var(--element-scale, 1))" : "24px";
            return {
                width: width,
                height: height,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: fontSize,
                fontWeight: "800",
                cursor: "pointer",
                border: "3px solid",
                boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
                transition: 'background-color .18s ease, box-shadow .18s ease',
                color: this.rgba(this.effective.textColor, false),
                borderColor: this.rgba(this.effective.outlineColor, false),
                backgroundColor: this.rgba(this.effective.buttonBackgroundColor),
                '--hover-bg': this.rgba(this.effective.buttonHoverColor)
            };
        },
        backIconStyles() {
            return this.getIconStylesFromSvg(Back2SVG);
        },
        backIconColor() {
            const effective = this.getEffectiveStyle();
            return this.rgba(effective.textColor, false);
        },
        prevIconStyles() {
            return this.getNavIconStyles('prev');
        },
        nextIconStyles() {
            return this.getNavIconStyles('next');
        },

        titleBarStyles() {
            const barH = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "56px";
            const radius = this.isStylePreview ? "6px" : "8px";
            const padding = this.isStylePreview ? "0 calc(18px * var(--element-scale, 1))" : "0 18px";
            const hidden = this.isTitleHidden();
            return {
                gridColumn: "2 / -1",
                height: barH,
                maxHeight: barH,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: radius,
                border: "3px solid",
                padding: padding,
                boxSizing: 'border-box',
                overflow: 'hidden',
                backgroundColor: this.rgba(this.effective.titleScreenBackgroundColor),
                borderColor: this.rgba(this.effective.outlineColor, false),
                boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
                visibility: hidden ? 'hidden' : 'visible',
                pointerEvents: hidden ? 'none' : 'auto'
            };
        },
        titleTextStyles() {
            const barH = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "56px";
            return {
                margin: 0,
                padding: 0,
                fontWeight: 800,
                letterSpacing: "0.4px",
                fontFamily: 'inherit',
                fontSize: "calc(var(--base-font) * var(--title-scale))",
                lineHeight: 1,
                color: this.rgba(this.effective.titleColor, false),
                maxHeight: barH,
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
            };
        },

        gridStyles() {
            const rowH = this.isStylePreview ? 48 : 56;
            const rowGap = this.isStylePreview ? 10 : 14;
            const colGap = this.isStylePreview ? 12 : 18;

            return {
                gridColumn: "2 / -1",
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gridTemplateRows: "repeat(4, var(--row-h))",
                gap: `${rowGap}px ${colGap}px`,
                alignContent: "start",
                justifyContent: "stretch",
                width: "100%",
                "--row-h": `${rowH}px`,
                "--row-gap": `${rowGap}px`,
                height: "calc(var(--row-h) * 4 + var(--row-gap) * 3)"
            };
        },

        pagerRowStyles() {
            return {
                gridColumn: "2 / -1",
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                gap: "12px",
                marginTop: this.isStylePreview ? "6px" : "10px",
            };
        },
        navBtnStyles() {
            const size = this.isStylePreview ? {w: 66, h: 56} : {w: 78, h: 64};
            return {
                width: `${size.w}px`,
                height: `${size.h}px`,
                margin: "0 auto",
                borderRadius: "10px",
                border: "3px solid",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: this.rgba(this.effective.buttonBackgroundColor),
                borderColor: this.rgba(this.effective.outlineColor, false),
                "--hover-bg": this.rgba(this.effective.buttonHoverColor),
                userSelect: "none",
            };
        },
    },
    watch: {
        displayTitleText(newVal) {
            this.syncEditableText('titleTextRef', newVal);
        },
        visibleButtonTexts: {
            handler(newTexts, oldTexts) {
                if (this.enableInlineEdit && Array.isArray(newTexts)) {
                    this.$nextTick(() => {
                        newTexts.forEach((displayText, index) => {
                            const opt = this.visibleOptions[index];
                            if (opt && opt.enabled !== false) {
                                const oldText = oldTexts && oldTexts[index];
                                if (oldText !== displayText) {
                                    this.syncButtonTextContent(index, displayText);
                                }
                            }
                        });
                    });
                }
            }
        }
    },
    methods: {
        goPrev() {
            if (this.currentPage > 1) {
                this.currentPage -= 1;
            }
        },
        goNext() {
            if (this.currentPage < this.totalPages) {
                this.currentPage += 1;
            }
        },
        goToPage(page) {
            this.currentPage = page;
        },
        getPageNumStyle(page) {
            const isActive = page === this.currentPage;
            const size = this.isStylePreview ? "28px" : "36px";
            const fontSize = this.isStylePreview ? "14px" : "16px";
            return {
                width: size,
                height: size,
                minWidth: size,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "6px",
                fontSize: fontSize,
                fontWeight: isActive ? 700 : 500,
                cursor: "pointer",
                border: "2px solid",
                transition: "all 0.2s ease",
                backgroundColor: isActive ? this.rgba(this.effective.buttonBackgroundColor) : this.rgba(this.effective.backgroundColor),
                color: this.rgba(this.effective.textColor, false),
                WebkitTextFillColor: this.rgba(this.effective.textColor, false),
                borderColor: this.rgba(this.effective.outlineColor, false),
                "--hover-bg": this.rgba(this.effective.buttonHoverColor),
                opacity: isActive ? 1 : 0.85,
            };
        },

        getOptionButtonStyles(opt) {
            const radius = this.isStylePreview ? "10px" : "12px";
            const hidden = this.isButtonHidden(opt);

            return {
                height: "var(--row-h)",
                padding: "10px 14px",
                borderRadius: radius,
                border: "3px solid",
                fontWeight: 500,
                fontSize: "calc(var(--base-font, 1rem) * var(--btn-scale, 1.1))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                wordBreak: "break-word",
                whiteSpace: "normal",
                textAlign: "center",
                letterSpacing: "0.2px",
                cursor: hidden ? 'default' : 'pointer',
                boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
                backgroundColor: this.rgba(this.effective.buttonBackgroundColor),
                color: this.rgba(this.effective.textColor, false),
                WebkitTextFillColor: this.rgba(this.effective.buttonTextColor, false),
                borderColor: this.rgba(this.effective.outlineColor, false),
                transition: "background-color .18s ease, box-shadow .18s ease",
                "--hover-bg": this.rgba(this.effective.buttonHoverColor),
                placeSelf: "stretch",
                visibility: hidden ? 'hidden' : 'visible',
                pointerEvents: hidden ? 'none' : 'auto'
            };
        },

        focusTitleText(event) {
            if (event.target.classList.contains('title-bar') || event.target.classList.contains('clickable-title')) {
                const el = this.$refs.titleTextRef;
                if (el) {
                    el.focus();
                    const range = document.createRange();
                    const sel = window.getSelection();
                    range.selectNodeContents(el);
                    range.collapse(false);
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            }
        },

        getDisplayButtonText(index) {
            return this.visibleButtonTexts[index] || '';
        },
        updateTitle(event) {
            this.updateTextField('titleText', event);
        },
        updateButton(index, event) {
            const element = event.target;
            const rawText = element.textContent || '';
            const newText = this.cleanText(rawText);
            const globalIndex = (this.currentPage - 1) * this.pageSize + index;
            const buttons = [...this.menuData.buttons || []];
            
            if (!buttons[globalIndex]) {
                buttons[globalIndex] = {};
            }

            if (!newText) {
                const fallbackValue = '\u200B';
                element.textContent = fallbackValue;
                const textEl = element.querySelector('.btn-text');
                if (textEl) textEl.textContent = ' ';
                buttons[globalIndex] = {...buttons[globalIndex], text: fallbackValue};
                this.$emit('update:menu-data', {
                    ...this.menuData,
                    buttons: buttons
                });
                return;
            }

            const currentText = this.cleanText(buttons[globalIndex].text);
            if (newText !== currentText) {
                buttons[globalIndex] = {...buttons[globalIndex], text: newText};
                this.$emit('update:menu-data', {
                    ...this.menuData,
                    buttons: buttons
                });
            }
        },

        isTitleHidden() {
            if (this.isStylePreview || this.isPreview || this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(this.menuData.titleText);
        },

        isButtonHidden(btn) {
            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                return false;
            }
            return btn.enabled === false || this.isEmptyText(btn.text);
        },
    },
};
</script>

<style scoped>
.infinite-menu {
    width: 100%;
    height: 100%;
    background: transparent;
    box-sizing: border-box;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    --base-font: 1rem;
    --title-scale: 1.6;
    --btn-scale: 1.1;
}

.cell:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.infinite-menu:not(.no-hover) .cell:hover {
    background-color: var(--hover-bg) !important;
}

.infinite-menu .back-btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.infinite-menu .back-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.infinite-menu:not(.no-hover) .back-btn:hover {
    background-color: var(--hover-bg) !important;
}

.nav:hover:not(.disabled) {
    background-color: var(--hover-bg) !important;
}

.infinite-menu:not(.no-hover) .nav:hover:not(.disabled) {
    background-color: var(--hover-bg) !important;
}

.nav.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
}

.page-num:hover {
    background-color: var(--hover-bg) !important;
}

.infinite-menu:not(.no-hover) .page-num:hover {
    background-color: var(--hover-bg) !important;
}

@media (max-width: 900px) {
    .grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
}

@media (max-width: 620px) {
    .grid {
        grid-template-columns: 1fr !important;
    }
}

.editable-text.is-editable {
    cursor: text;
    position: relative;
    outline: none;
}

.editable-text.is-editable:hover {
    background-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.4);
}

.editable-text.is-editable:focus {
    background-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.7);
}

.title-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    max-height: 3em !important;
    text-align: center !important;
}

.cell {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    word-break: break-word !important;
    white-space: normal !important;
    min-height: 60px !important;
    max-height: none !important;
    box-sizing: border-box !important;
}

.cell .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.cell[contenteditable="true"] {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.cell[contenteditable="true"] .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.cell-disabled {
    visibility: hidden !important;
    pointer-events: none !important;
}

.back-icon-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.back-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0; /* Hide the actual SVG, we'll use the mask instead */
}

.nav-icon-wrapper {
    width: 100%;
    height: 70%;
    position: relative;
    margin: auto 0;
}

.nav-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0; /* Hide the actual SVG, we'll use the mask instead */
}
</style>
